/** @file The import dialog's body per step: reading, choosing (layers, placement, notes), progress, result. */
import type { Translate } from '@pose2d/i18n/types';
import { errorText } from '../shell/i18n/errorText';
import { ImportNameField } from './ImportNameField';
import { NOTES_MAX } from './notesMax.data';
import type { ImportDraft, ImportSession, ImportStep } from './importTypes';
import { LayerChoiceList } from './LayerChoiceList';
import { PlacementPreview } from './PlacementPreview';
import { WarningList } from './WarningList';

type Open = Exclude<ImportStep, { kind: 'closed' }>;

function Choose({ t, draft, session }: { t: Translate; draft: ImportDraft; session: ImportSession }) {
  const { target, source } = draft;
  return (
    <div className="import-choose">
      {target.kind === 'newProject' ? <ImportNameField t={t} name={target.name} session={session} /> : null}
      {source.kind === 'png' ? (
        <label className="import-place"><input type="checkbox" checked={draft.place} onChange={(e) => { session.setPlace(e.currentTarget.checked); }} />{t('psdImport.place')}</label>
      ) : null}
      <LayerChoiceList t={t} draft={draft} session={session} />
      <PlacementPreview t={t} draft={draft} />
      {source.kind === 'psd' ? <WarningList t={t} title={t('psdImport.notes', { n: source.session.warnings.length })} warnings={source.session.warnings} max={NOTES_MAX} /> : null}
    </div>
  );
}

function Failed({ t, step, session }: { t: Translate; step: Extract<ImportStep, { kind: 'failed' }>; session: ImportSession }) {
  const total = step.draft?.choices.filter((c) => c.checked).length ?? 0;
  const target = step.draft?.target;
  return (
    <>
      <div role="alert" className="shell-error">
        {step.draft === null ? null : <p>{t('psdImport.failed', { done: step.draft.uploaded.size, total })}</p>}
        <p>{errorText(t, step.thrown)}</p>
      </div>
      {target?.kind === 'newProject' ? <ImportNameField t={t} name={target.name} session={session} /> : null}
    </>
  );
}

/** Pure view of one open step; every action goes to `session`. */
export function ImportStepBody({ t, step, session }: { t: Translate; step: Open; session: ImportSession }) {
  switch (step.kind) {
    case 'reading':
      return <p className="import-note">{t('psdImport.reading', { file: step.fileName })}</p>;
    case 'choose':
      return <Choose t={t} draft={step.draft} session={session} />;
    case 'uploading':
      return <p className="import-note" role="status">{t('psdImport.uploading', { done: step.done, total: step.total })}<progress value={step.done} max={step.total} /></p>;
    case 'done':
      return (
        <div className="import-done" role="status">
          <p>{t('psdImport.done', { n: step.outcome.images, rev: step.outcome.rev })}</p>
          {step.outcome.renamed > 0 ? <p>{t('messages.renamedLayers', { n: step.outcome.renamed })}</p> : null}
          {step.outcome.deduplicated > 0 ? <p>{t('messages.renamedUnique', { n: step.outcome.deduplicated })}</p> : null}
          <WarningList t={t} title={t('psdImport.notes', { n: step.outcome.warnings.length })} warnings={step.outcome.warnings} max={NOTES_MAX} />
        </div>
      );
    case 'failed':
      return <Failed t={t} step={step} session={session} />;
  }
}
